import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Mail, Lock, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth, LanguageToggle, API } from '../App';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';

const LoginPage = () => {
  const { t, i18n } = useTranslation();
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [bgImage, setBgImage] = useState('https://images.unsplash.com/photo-1726906741385-c114d7fdd845?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwzfHxoYWlyJTIwZmFzaGlvbiUyMHJ1bndheSUyMGRhcmt8ZW58MHx8fHwxNzcyMDg1OTA0fDA&ixlib=rb-4.1.0&q=85');

  const from = location.state?.from?.pathname || '/';

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API}/settings`);
        if (res.data.login_bg_image_url) {
          setBgImage(res.data.login_bg_image_url);
        }
      } catch (e) {
        console.error('Failed to fetch settings', e);
      }
    };
    fetchSettings();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success(t('common.success'));
      navigate(from, { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-misel-bg flex">
      {/* Left side - Image */}
      <div 
        className="hidden lg:flex lg:w-1/2 relative bg-cover bg-center"
        style={{
          backgroundImage: `url('${bgImage}')`
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-misel-bg via-misel-bg/50 to-transparent" />
        <div className="relative z-10 p-12 flex flex-col justify-between h-full">
          <Link to="/" className="flex items-center gap-2 text-white/70 hover:text-white transition-colors">
            <ArrowLeft size={20} />
            <span className="font-roboto font-bold tracking-wider">MISEL GROUP DIGITAL LAB</span>
          </Link>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          {/* Mobile back button */}
          <Link 
            to="/" 
            className="lg:hidden flex items-center gap-2 text-white/70 hover:text-white transition-colors mb-8"
          >
            <ArrowLeft size={20} />
            <span className="font-roboto font-bold tracking-wider">MISEL GROUP DIGITAL LAB</span>
          </Link>

          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="font-roboto font-bold text-3xl text-white tracking-wide mb-2">
                {t('auth.login_title')}
              </h1>
              <p className="text-gray-400">
                {t('auth.login_subtitle')}
              </p>
            </div>
            <LanguageToggle />
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <label className="text-sm text-gray-400">{t('auth.email')}</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-primary"
                  placeholder="email@example.com"
                  required
                  data-testid="login-email"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm text-gray-400">{t('auth.password')}</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-primary"
                  placeholder="••••••••"
                  required
                  data-testid="login-password"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="w-full bg-primary hover:bg-primary/90 text-white font-semibold tracking-wide uppercase py-6"
            >
              {loading ? t('common.loading') : t('auth.login_btn')}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <Link to="/forgot-password" className="text-gray-400 hover:text-white text-sm transition-colors" data-testid="forgot-password-link">
              {t('auth.forgot_password')}
            </Link>
          </div>

          <div className="mt-4 text-center">
            <p className="text-gray-400">
              {t('auth.no_account')}{' '}
              <Link to="/register" className="text-primary hover:underline" data-testid="register-link">
                {t('auth.register_btn')}
              </Link>
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default LoginPage;
